<template>
  <div>
    <!-- Toolbar -->
    <div class="tabToolbar">
      <div style="position: relative; flex: 0 0 240px;">
        <input
          class="input"
          type="text"
          v-model="q"
          placeholder="Search email"
          @keydown.enter.prevent="reload"
        />
      </div>
      <SearchSelect
        v-model="selectedAccountId"
        :optionsFunction="searchAccountsById"
        :maxResults="20"
        placeholder="All accounts"
        search-placeholder="Search accounts…"
        clear-label="All accounts"
        style="min-width: 200px; flex: 1; max-width: 300px;"
        @update:modelValue="scheduleReload"
      />
      <button class="btn" @click="showAdd = true" :disabled="saving">Add unsubscribe</button>
    </div>

    <div v-if="error" class="error" style="margin-bottom: 10px;">{{ error }}</div>
    <div v-if="success" class="successMsg">{{ success }}</div>

    <div v-if="loading && rows.length === 0" class="emptyMsg">Loading…</div>
    <div v-else-if="rows.length === 0" class="emptyMsg">No unsubscribes.</div>
    <div v-else class="tableWrap">
      <table class="table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Source</th>
            <th>Unsubscribed at</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.id">
            <td class="mono">{{ r.email }}</td>
            <td>{{ r.source || '—' }}</td>
            <td class="mono">{{ formatDt(r.unsubscribed_at) }}</td>
            <td style="text-align: right;">
              <button class="btn danger" @click="remove(r)" :disabled="saving">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="rows.length > 0" class="tableFooter">
      <span class="hint">{{ rows.length }} unsubscribe{{ rows.length === 1 ? '' : 's' }}</span>
    </div>

    <Modal v-if="showAdd" title="Add unsubscribe" @close="showAdd = false">
      <div class="field">
        <label>Email</label>
        <input class="input" type="text" v-model="newEmail" placeholder="name@company.com" @keydown.enter.prevent="add" />
      </div>
      <template #footer>
        <button class="btn" @click="showAdd = false" :disabled="saving">Cancel</button>
        <button class="btn primary" @click="add" :disabled="saving || !newEmail.trim()">Add</button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import Modal from '../Modal.vue'
import SearchSelect from '../SearchSelect.vue'
import { apiFetch } from '../../services/api.ts'

const error = ref('')
const success = ref('')
const loading = ref(false)
const saving = ref(false)

const selectedAccountId = ref(null)

async function searchAccountsById(query) {
  const qs = new URLSearchParams({ q: query, limit: '20' })
  const resp = await apiFetch(`/accounts/paginated?${qs}`)
  const accts = Array.isArray(resp?.items) ? resp.items : []
  return accts.map((a) => ({
    value: String(a.id || ''),
    label: String(a.name || a.domain || '').trim() || String(a.domain || ''),
    domain: String(a.domain || ''),
  }))
}
const q = ref('')
const newEmail = ref('')
const showAdd = ref(false)
const reloadTimer = ref(null)
const rows = ref([])

function formatDt(v) {
  const s = String(v || '')
  return s ? s.replace('T', ' ').replace('Z', '') : ''
}

function normalizeRow(r) {
  const o = r && typeof r === 'object' ? r : {}
  return {
    id: o.id,
    email: typeof o.email === 'string' ? o.email : '',
    source: typeof o.source === 'string' ? o.source : '',
    unsubscribed_at: o.unsubscribed_at,
  }
}

async function reload() {
  loading.value = true
  error.value = ''
  success.value = ''
  try {
    const params = new URLSearchParams()
    const qs = String(q.value || '').trim()
    if (qs) params.set('q', qs)
    if (selectedAccountId.value) params.set('account_id', String(selectedAccountId.value))
    params.set('limit', '200')
    const resp = await apiFetch(`/unsubscribes?${params.toString()}`)
    const list = Array.isArray(resp?.unsubscribes) ? resp.unsubscribes : []
    rows.value = list.map(normalizeRow).filter((x) => x.id && x.email)
  } catch (e) {
    error.value = e?.message || 'Failed'
  } finally {
    loading.value = false
  }
}


async function add() {
  const email = String(newEmail.value || '').trim()
  if (!email) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await apiFetch('/unsubscribes', { method: 'POST', body: { email } })
    newEmail.value = ''
    success.value = 'Added'
    showAdd.value = false
    await reload()
  } catch (e) {
    error.value = e?.message || 'Failed'
  } finally {
    saving.value = false
  }
}

async function remove(r) {
  const id = r?.id
  if (!id) return
  if (!confirm(`Remove unsubscribe for ${String(r?.email || '')}?`)) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await apiFetch(`/unsubscribes/${encodeURIComponent(String(id))}`, { method: 'DELETE' })
    success.value = 'Removed'
    await reload()
  } catch (e) {
    error.value = e?.message || 'Failed'
  } finally {
    saving.value = false
  }
}

function scheduleReload() {
  if (reloadTimer.value) clearTimeout(reloadTimer.value)
  reloadTimer.value = setTimeout(async () => {
    reloadTimer.value = null
    await reload()
  }, 250)
}

watch([selectedAccountId, q], scheduleReload)

watch(showAdd, (open) => {
  if (!open) return
  error.value = ''
  success.value = ''
  newEmail.value = ''
})

onMounted(async () => {
  await reload()
})
</script>

<style scoped>
.tabToolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  flex-wrap: wrap;
}

.input {
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
  font-size: 13px;
  padding: 0 10px;
  width: 100%;
}

.input:focus {
  outline: none;
  border-color: var(--accent);
}

.tableWrap {
  overflow-x: auto;
  width: 100%;
}

.tableFooter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
}

.hint {
  color: var(--muted);
  font-size: 12px;
}

.emptyMsg {
  color: var(--muted);
  font-size: 13px;
  padding: 20px 0;
}

.error {
  color: var(--danger);
  font-size: 13px;
}

.successMsg {
  color: var(--success, #059669);
  font-size: 13px;
  margin-bottom: 10px;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 12px;
  font-weight: 700;
  color: var(--table-header-text);
}
</style>
